import type { FC } from "react";
import { Suspense, useCallback, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { Outlet, useLocation } from "react-router-dom";
import { useAuthenticateToken } from "modules/user";
import Login from "./components/Login";
import Header from "components/ui/layout/Header";
import Footer from "components/ui/layout/Footer";
import { useSelector } from "react-redux";
import type { RootState } from "StoreTypes";
import ServiceRequestTray from "components/ui/layout/ServiceRequestTray";
import { Toaster } from "react-hot-toast";
import { connection } from "utils/websockets/websocketMiddleware";
import { HubConnectionState } from "updated-redux-signalr";
import HelpDocTray from "components/shared/helpdocs/HelpDocTray";
import {
  LocalStorageItems,
  setLocalStorageValue,
} from "utils/hooks/localStorage";
import { getCurrentFacility } from "modules/facility/facilitySelectors";
import { ModalType } from "utils/modals/modalHelpers";
import { useLDClient } from "launchdarkly-react-client-sdk";
import ScorecardSearchModal from "components/scorecard/ScorecardSearchModal";
import CountdownTimer from "components/control/dashboard/timers/CountdownTimer";
import { getSimulatorsByCurrentFacility } from "modules/simulator/simulatorSelectors";
import RexReservationModal from "components/rex-reservations/RexReservationModal";
import { hasClubhouseAdminClaim } from "utils/roles/rolesAndClaimsHelpers";
import { setClubhouseAdmin } from "modules/user/userSlice";
import { useAppDispatch } from "utils/hooks";
import InactivityLogoutTimer from "./InactivityLogoutTimer";
import "react-datepicker/dist/react-datepicker.css";
import { getCurrentUserToken } from "utils/tokens/tokenHelpers";
import { SidebarInset } from "./components/ui/sidebar";
import { SidebarProvider } from "./components/ui/sidebar";
import { AppSidebar } from "./components/app-sidebar";

const PageLoader: FC = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 dark:border-zinc-400" />
  </div>
);

//const ENABLE_COMPETITIONS_DEBUGGER = process.env.NODE_ENV !== 'production';

const TrackPageViews = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);

  return null;
};

const App: FC = () => {
  useAuthenticateToken(true);

  const ldClient = useLDClient();
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.user);
  const isAuthenticated = user?.isAuthenticated;
  const currentFacility = useSelector((state: RootState) =>
    getCurrentFacility(state),
  );
  const browserId = localStorage.getItem(LocalStorageItems.BROWSERID);

  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDarkMode = localStorage.getItem(LocalStorageItems.DARKMODE)
    ? localStorage.getItem(LocalStorageItems.DARKMODE) === "true"
    : prefersDark;

  const isScorecardSearchModalOpen = useSelector(
    (state: RootState) =>
      state.modals?.modalSettings[ModalType.ScorecardSearch]?.isOpen,
  );
  const isRexReservationModalOpen = useSelector(
    (state: RootState) =>
      state.modals?.modalSettings[ModalType.RexReservationModal]?.isOpen,
  );

  const startTimedConnectionRef = useRef<
    (facilityId: string) => void | Promise<void>
  >(async () => {});

  const startTimedConnection = useCallback(
    async (currentFacilityId: string) => {
      if (connection) {
        await connection.start();
        await connection.invoke(
          "LinkFacilityBrowserToConnectionId",
          currentFacilityId,
          browserId,
        );
      }
      setTimeout(() => {
        void connection.stop().then(() => {
          void startTimedConnectionRef.current(currentFacilityId);
        });
      }, 300000);
    },
    [browserId],
  );

  useEffect(() => {
    startTimedConnectionRef.current = startTimedConnection;
  }, [startTimedConnection]);

  useEffect(() => {
    if (
      isAuthenticated &&
      currentFacility &&
      (connection.state === HubConnectionState.Disconnected ||
        connection.state === HubConnectionState.Disconnecting)
    ) {
      void startTimedConnection(currentFacility.id);
    }
  }, [isAuthenticated, startTimedConnection, currentFacility]);

  const simulators = useSelector(getSimulatorsByCurrentFacility);

  useEffect(() => {
    window.document.documentElement.classList.toggle("dark", isDarkMode);
    void setLocalStorageValue(
      LocalStorageItems.DARKMODE,
      JSON.stringify(isDarkMode),
    );
  }, [isDarkMode]);

  // Pendo ignores repeat `initialize` calls, so the first valid identity
  // initializes the agent and later changes (a facility switch) re-identify.
  const pendoInitialized = useRef(false);
  useEffect(() => {
    const userProperties = getCurrentUserToken();
    if (!userProperties || !window.pendo || !currentFacility) return;
    const identity = {
      visitor: {
        id: userProperties.email,
        email: userProperties.email,
        full_name: userProperties.userName || "",
        user_id: userProperties.userId,
        roles: userProperties.userRoles?.join(","),
      },
      account: {
        id: currentFacility.id,
        name: currentFacility.name,
        clubhouseFacilityId: currentFacility.clubhouseFacilityId,
        timeZone: currentFacility.timeZoneIana,
        canUseAdKit: currentFacility.capabilities?.canUseAdKit,
      },
    };
    if (pendoInitialized.current) {
      window.pendo.identify?.(identity);
    } else {
      window.pendo.initialize?.(identity);
      pendoInitialized.current = true;
    }
  }, [user, currentFacility]);

  useEffect(() => {
    const userProperties = getCurrentUserToken();

    if (userProperties && currentFacility) {
      const userContext = {
        kind: "user",
        anonymous: false,
        key: userProperties.userId,
        name: userProperties.userName,
        email: userProperties.email,
        facilities: userProperties.facilities,
        roles: userProperties.userRoles,
      };
      const facilityContext = {
        kind: "facility",
        anonymous: false,
        key: currentFacility.id,
        name: currentFacility.name,
      };
      const context = {
        kind: "multi",
        user: userContext,
        facility: facilityContext,
      };

      void ldClient?.identify(context, undefined, (err) => {
        if (err) {
          console.error("LD identify failed:", err);
          return;
        }

        void ldClient.flush();
      });
    }
  }, [user, currentFacility, ldClient]);

  useEffect(() => {
    const userProperties = getCurrentUserToken();

    if (userProperties && !user?.clubhouseAdmin && currentFacility?.id) {
      void hasClubhouseAdminClaim(currentFacility.id).then(
        (clubhouseAdminAccess) => {
          dispatch(setClubhouseAdmin({ clubhouseAdmin: clubhouseAdminAccess }));
        },
      );
    }
  }, [currentFacility?.id, dispatch, user]);

  return (
    <SidebarProvider
      style={
        {
          // LAYOUT PROTOTYPE: 18rem matches the shadcn dashboard reference (was
          // 16rem) — a touch roomier for nav labels. Mobile drawer stays 16rem.
          "--sidebar-width": "18rem",
          "--sidebar-width-mobile": "16rem",
        } as React.CSSProperties
      }
      defaultOpen={true}
    >
      {isAuthenticated ? (
        <>
          <AppSidebar variant="inset" />
          <SidebarInset className="min-h-svh">
            <Header />
            <div className="flex flex-1 flex-col">
              <div className="@container/main flex flex-1 flex-col gap-2">
                <div className="flex flex-col gap-4 pb-4 md:gap-6 md:pb-6">
                  <Suspense fallback={<PageLoader />}>
                    <Outlet />
                  </Suspense>
                  {isScorecardSearchModalOpen && (
                    <ScorecardSearchModal
                      modalName={ModalType.ScorecardSearch}
                    />
                  )}
                  {isRexReservationModalOpen && (
                    <RexReservationModal
                      modalName={ModalType.RexReservationModal}
                    />
                  )}
                  <ServiceRequestTray />
                  <HelpDocTray />
                  <Footer />
                  <InactivityLogoutTimer />
                  <TrackPageViews />

                  <Toaster
                    position="bottom-left"
                    containerClassName="z-9999"
                    toastOptions={{ style: { zIndex: 9999 } }}
                  />
                </div>
                {simulators?.map((sim) => (
                  <CountdownTimer simulatorId={sim.id} key={sim.id} />
                ))}
              </div>
            </div>
          </SidebarInset>
        </>
      ) : (
        <Login />
      )}
    </SidebarProvider>
  );
};
export default App;
